import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ShopProvider } from "./context/ShopContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import { Login, Register } from "./pages/Auth";
import ScrollToTop from "./components/ScrollToTop";
import SeoCanonical from "./components/SeoCanonical";
import CompareTray from "./components/CompareTray";
import { CompareProvider } from "./context/CompareContext";
import { applyTheme, lang } from "./lib/i18n";

applyTheme();
document.documentElement.lang = lang();

const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Orders = lazy(() => import("./pages/Orders"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const Seller = lazy(() => import("./pages/Seller"));
const OrderSuccess = lazy(() => import("./pages/OrderSuccess"));
const Invoice = lazy(() => import("./pages/Invoice"));
const Rider = lazy(() => import("./pages/Rider"));
const Admin = lazy(() => import("./pages/Admin"));
const Compare = lazy(() => import("./pages/Compare"));
const Help = lazy(() => import("./pages/Help"));
const About = lazy(() => import("./pages/About"));
const ScratchPopup = lazy(() => import("./components/ScratchPopup"));
const GroceryList = lazy(() => import("./pages/GroceryList"));
const Policy = lazy(() => import("./pages/Policy"));
const Account = lazy(() => import("./pages/Account"));
const Notifications = lazy(() => import("./pages/Notifications"));
const ForgotPassword = lazy(() => import("./pages/PasswordReset").then((m) => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import("./pages/PasswordReset").then((m) => ({ default: m.ResetPassword })));

function Protected({ children, seller, role }) {
  const { user } = useAuth();
  if (user === null) return <div className="p-20 text-center text-slate-400" data-testid="auth-loading">Loading...</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: window.location.pathname + window.location.search }} />;
  if (seller && !["seller", "admin", "employee"].includes(user.role)) return <Navigate to="/" replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
}

const RIDER_OK = /^\/(rider|account|help|notifications|policies\/[^/]+|login|reset-password|forgot-password)$/;

function RiderGate({ children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (user?.role === "rider" && !RIDER_OK.test(pathname)) return <Navigate to="/rider" replace />;
  if (user?.role === "employee" && !/^\/(seller|account|login|notifications)$/.test(pathname)) return <Navigate to="/seller" replace />;
  return children;
}

function Chrome() {
  const { user } = useAuth();
  if (["rider", "employee"].includes(user?.role)) return null;
  return <><Footer /><CompareTray /><Suspense fallback={null}><ScratchPopup /></Suspense></>;
}

function App() {
  return (
    <div className="App grain">
      <BrowserRouter>
        <ScrollToTop />
        <SeoCanonical />
        <AuthProvider>
          <ShopProvider>
            <CompareProvider>
            <Header />
            <main className="relative z-[2] min-h-[70vh] overflow-x-clip">
              <Suspense fallback={<div className="p-20 text-center text-slate-500" data-testid="page-loading">Loading...</div>}>
              <RiderGate>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/products" element={<Products />} />
                <Route path="/category/:cat" element={<Products />} />
                <Route path="/compare" element={<Compare />} />
                <Route path="/product/:id" element={<ProductDetail />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/policies/:slug" element={<Policy />} />
                <Route path="/help" element={<Help />} />
                <Route path="/about" element={<About />} />
                <Route path="/account" element={<Protected><Account /></Protected>} />
                <Route path="/cart" element={<Protected><Cart /></Protected>} />
                <Route path="/checkout" element={<Protected><Checkout /></Protected>} />
                <Route path="/orders" element={<Protected><Orders /></Protected>} />
                <Route path="/grocery-list" element={<Protected><GroceryList /></Protected>} />
                <Route path="/order-success/:id" element={<Protected><OrderSuccess /></Protected>} />
                <Route path="/wishlist" element={<Protected><Wishlist /></Protected>} />
                <Route path="/seller" element={<Protected seller><Seller /></Protected>} />
                <Route path="/invoice/:id" element={<Protected><Invoice /></Protected>} />
                <Route path="/rider" element={<Protected role="rider"><Rider /></Protected>} />
                <Route path="/admin" element={<Protected role="admin"><Admin /></Protected>} />
                <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
              </Routes>
              </RiderGate>
              </Suspense>
            </main>
            <Chrome />
            <Toaster theme="dark" position="top-center" richColors />
            </CompareProvider>
          </ShopProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
